/**
 * The generic, library-agnostic charting IR shared across features.
 *
 * A {@link ChartDataset} is "chart-ready" data (a category key + rows + the y
 * series + resolved colors + a value formatter); {@link buildAgOptions} turns
 * one into brand-themed AG Charts options. Charts Lab builds datasets from the
 * KPI payload; Reports builds them from an agent query result + a viz spec.
 * Keeping this here (not in a feature) is what lets both plot identical shapes.
 */
import type {
  AgAxisLabelFormatterParams,
  AgBarSeriesOptions,
  AgCartesianAxisOptions,
  AgCartesianChartOptions,
  AgCartesianSeriesOptions,
  AgChartOptions,
  AgPolarSeriesOptions,
} from "ag-charts-community";

import { cinchAgTheme, DEFAULT_PALETTE_ORDER, resolveCssColor, resolveToken } from "./agTheme";

export type ChartType =
  | "line"
  | "area"
  | "stacked-area"
  | "bar"
  | "horizontal-bar"
  | "stacked-bar"
  | "scatter"
  | "bubble"
  | "pie"
  | "donut";

export const CHART_TYPE_LABEL: Record<ChartType, string> = {
  line: "Line",
  area: "Area",
  "stacked-area": "Stacked area",
  bar: "Bar",
  "horizontal-bar": "Horizontal bar",
  "stacked-bar": "Stacked bar",
  scatter: "Scatter",
  bubble: "Bubble",
  pie: "Pie",
  donut: "Donut",
};

export interface DatasetSeries {
  key: string;
  name: string;
  /** Per-series chart-type override for combination charts (bars + a line
   *  etc.); honored when the base chart type is line/area/bar, else ignored. */
  type?: "line" | "area" | "bar";
}

/** A trend/reference overlay for a cartesian chart. AG Charts has no built-in
 *  trend line (Community or Enterprise), so we compute it and draw it as an
 *  overlay line series (linear/moving-average) or a constant axis cross-line
 *  (reference). */
export interface TrendSpec {
  type: "linear" | "moving-average" | "reference";
  /** moving-average window in points (default 3). */
  window?: number;
  /** reference: the series mean, or a fixed value. */
  mode?: "average" | "value";
  /** reference value when mode === "value". */
  value?: number;
  /** which series the trend follows (defaults to the first). */
  seriesKey?: string;
}

export interface ChartDataset {
  /** Stable id for the dataset (free-form; not interpreted). */
  key: string;
  label: string;
  description?: string;
  kind: "timeseries" | "category";
  /** Chart types that make sense for this dataset's shape. */
  chartTypes: ChartType[];
  /** The x/category field name on each row. */
  categoryKey: string;
  /** One row per x value; each row has categoryKey + every series key. */
  rows: Record<string, string | number | null>[];
  /** The y series to plot. */
  series: DatasetSeries[];
  /** Concrete colors, aligned to `series` (timeseries) or to `rows` (category). */
  colors: string[];
  /** Formats a y value for axes and tooltips. */
  valueFormat: (n: number) => string;
  /** Optional trend/reference overlay (cartesian only). */
  trend?: TrendSpec;
}

// ─── value formats (the conventions the "Chart Design" tab documents) ────────

export type ValueFormat = "usd" | "usd_compact" | "pct" | "count" | "plain";

export const VALUE_FORMAT_LABEL: Record<ValueFormat, string> = {
  usd: "Currency ($1,234)",
  usd_compact: "Currency compact ($1.2K / $3.4M)",
  pct: "Percent (12.3%)",
  count: "Count (1,234)",
  plain: "Plain number",
};

function compactUsd(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${Math.round(n).toLocaleString("en-US")}`;
}

/** A number→string formatter for a named convention. Reports maps its viz
 *  spec's `valueFormat` string through this; the DS tab lists them all. */
export function makeValueFormatter(kind: ValueFormat): (n: number) => string {
  switch (kind) {
    case "usd":
      return (n) => `$${Math.round(n).toLocaleString("en-US")}`;
    case "usd_compact":
      return compactUsd;
    case "pct":
      return (n) => `${n.toFixed(1)}%`;
    case "count":
      return (n) => Math.round(n).toLocaleString("en-US");
    case "plain":
    default:
      return (n) => n.toLocaleString("en-US");
  }
}

// ─── AG Charts options builder ──────────────────────────────────────────────

/**
 * The colors to cycle, most-specific first: the dataset's own colors, then the
 * rest of the brand palette so there are always enough distinct hues. Without
 * this a length-1 dataset palette (e.g. a one-series pie) painted every slice
 * the same brand blue.
 */
function effectivePalette(colors: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const c of [...colors, ...DEFAULT_PALETTE_ORDER.map(resolveToken)]) {
    if (c && !seen.has(c)) {
      seen.add(c);
      out.push(c);
    }
  }
  return out.length > 0 ? out : colors;
}

function cartesianSeries(
  ds: ChartDataset,
  chartType: ChartType,
  palette: string[],
): AgCartesianSeriesOptions[] {
  const pick = (i: number) => palette[i % palette.length]!;
  // A single-series *category* chart (e.g. "count by schema") reads better with
  // one color per bar; a single time series stays a single brand color.
  const perCategory = ds.series.length === 1 && ds.kind === "category";
  const catColor = new Map<string, string>();
  if (perCategory) {
    ds.rows.forEach((r, i) => catColor.set(String(r[ds.categoryKey] ?? ""), pick(i)));
  }

  // Combination charts: when the base is a plain line/area/bar, each series may
  // override its own type (e.g. bars + a line); specialized bases (stacked-*,
  // horizontal-bar) apply to every series.
  const combinable = chartType === "line" || chartType === "area" || chartType === "bar";

  return ds.series.map((s, i): AgCartesianSeriesOptions => {
    const color = pick(i);
    const common = { xKey: ds.categoryKey, yKey: s.key, yName: s.name } as const;
    const eff: ChartType = combinable && s.type ? s.type : chartType;
    switch (eff) {
      case "area":
      case "stacked-area":
        return {
          type: "area",
          ...common,
          fill: color,
          stroke: color,
          fillOpacity: 0.35,
          ...(eff === "stacked-area" ? { stacked: true } : {}),
        };
      case "bar":
      case "horizontal-bar":
      case "stacked-bar": {
        const bar: AgBarSeriesOptions = {
          type: "bar",
          ...common,
          fill: color,
          stroke: color,
          ...(eff === "stacked-bar" ? { stacked: true } : {}),
          ...(eff === "horizontal-bar" ? { direction: "horizontal" } : {}),
        };
        if (perCategory) {
          bar.itemStyler = (p) => {
            const row = p.datum as Record<string, unknown>;
            const c = catColor.get(String(row[ds.categoryKey] ?? "")) ?? color;
            return { fill: c, stroke: c };
          };
        }
        return bar;
      }
      case "line":
      default:
        return {
          type: "line",
          ...common,
          stroke: color,
          marker: { enabled: true, size: 6, fill: color, stroke: color },
        };
    }
  });
}

/** Scatter / bubble series — an x/y (numeric x) shape distinct from the
 *  category charts. Scatter plots one series per y column; bubble is a single
 *  series with the first y as position and the second (if any) as bubble size. */
function xySeries(
  ds: ChartDataset,
  chartType: ChartType,
  palette: string[],
): AgCartesianSeriesOptions[] {
  const pick = (i: number) => palette[i % palette.length]!;
  if (chartType === "bubble") {
    const yKey = ds.series[0]?.key ?? ds.categoryKey;
    const sizeKey = ds.series[1]?.key ?? yKey; // fall back to sizing by the y value
    const color = pick(0);
    return [
      {
        type: "bubble",
        xKey: ds.categoryKey,
        yKey,
        sizeKey,
        yName: ds.series[0]?.name ?? yKey,
        fill: color,
        stroke: color,
        fillOpacity: 0.6,
      },
    ];
  }
  return ds.series.map((s, i) => ({
    type: "scatter",
    xKey: ds.categoryKey,
    yKey: s.key,
    yName: s.name,
    fill: pick(i),
    stroke: pick(i),
  }));
}

function polarSeries(
  ds: ChartDataset,
  chartType: ChartType,
  palette: string[],
): AgPolarSeriesOptions[] {
  const angleKey = ds.series[0]?.key ?? "value";
  // One color per slice (cycled), so every slice + legend item is distinct.
  const fills = ds.rows.map((_, i) => palette[i % palette.length]!);
  return [
    {
      type: chartType === "donut" ? "donut" : "pie",
      angleKey,
      calloutLabelKey: ds.categoryKey,
      legendItemKey: ds.categoryKey,
      fills,
      strokes: fills,
      ...(chartType === "donut" ? { innerRadiusRatio: 0.6 } : {}),
    },
  ];
}

// ─── trend / reference overlays ─────────────────────────────────────────────

/** The y values of a series as numbers (null for blanks / non-numeric). */
function seriesNumbers(ds: ChartDataset, key: string): (number | null)[] {
  return ds.rows.map((r) => {
    const v = r[key];
    if (v == null || v === "") return null;
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : null;
  });
}

export function trendLabel(trend: TrendSpec): string {
  if (trend.type === "linear") return "Trend";
  if (trend.type === "moving-average") return `${trend.window ?? 3}-pt avg`;
  return trend.mode === "value" ? "Target" : "Average";
}

/** Per-row trend values for a linear regression (least squares over the row
 *  index) or a trailing moving average. Returns null if it can't be computed. */
export function trendValues(ys: (number | null)[], trend: TrendSpec): (number | null)[] | null {
  if (trend.type === "linear") {
    const pts = ys
      .map((y, i) => [i, y] as const)
      .filter((p): p is readonly [number, number] => p[1] != null);
    if (pts.length < 2) return null;
    const n = pts.length;
    const sx = pts.reduce((a, [x]) => a + x, 0);
    const sy = pts.reduce((a, [, y]) => a + y, 0);
    const sxx = pts.reduce((a, [x]) => a + x * x, 0);
    const sxy = pts.reduce((a, [x, y]) => a + x * y, 0);
    const denom = n * sxx - sx * sx;
    if (denom === 0) return null;
    const slope = (n * sxy - sx * sy) / denom;
    const intercept = (sy - slope * sx) / n;
    return ys.map((_, i) => slope * i + intercept);
  }
  if (trend.type === "moving-average") {
    const w = Math.max(2, trend.window ?? 3);
    return ys.map((_, i) => {
      if (i < w - 1) return null;
      const win = ys.slice(i - w + 1, i + 1);
      if (win.some((v) => v == null)) return null;
      return (win as number[]).reduce((a, b) => a + b, 0) / w;
    });
  }
  return null;
}

/** The constant value for a reference line (series mean, or a fixed value). */
export function referenceValue(ys: (number | null)[], trend: TrendSpec): number | null {
  if (trend.type !== "reference") return null;
  if (trend.mode === "value") return typeof trend.value === "number" ? trend.value : null;
  const vals = ys.filter((v): v is number => v != null);
  return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
}

/** Build a complete, brand-themed AgChartOptions for a dataset + chart type. */
export function buildAgOptions(
  ds: ChartDataset,
  chartType: ChartType,
  isDark: boolean,
): AgChartOptions {
  const palette = effectivePalette(ds.colors);
  const legendEnabled = ds.series.length > 1 || ds.kind === "category";
  const base = {
    theme: cinchAgTheme(palette, isDark),
    // Blend into the surrounding Card instead of painting a solid panel.
    background: { visible: false },
    // Off by default: snappier when flipping datasets/types, and it lets the
    // canvas settle to an idle frame (otherwise the rAF loop never quiesces).
    animation: { enabled: false },
    data: ds.rows,
    legend: { enabled: legendEnabled },
  };

  if (ds.kind === "category" && (chartType === "pie" || chartType === "donut")) {
    return { ...base, series: polarSeries(ds, chartType, palette) };
  }

  const isXY = chartType === "scatter" || chartType === "bubble";
  const horizontal = chartType === "horizontal-bar";
  const valueFmt = (p: AgAxisLabelFormatterParams) => ds.valueFormat(Number(p.value));

  // The xKey axis: category (numeric for scatter/bubble); on the left for
  // horizontal bars, with the value axis then on the bottom.
  const xAxis: AgCartesianAxisOptions = {
    type: isXY ? "number" : "category",
    position: horizontal ? "left" : "bottom",
  };
  const yAxis: AgCartesianAxisOptions = {
    type: "number",
    position: horizontal ? "bottom" : "left",
    label: { formatter: valueFmt },
  };

  // Trend / reference overlays apply to the standard category charts only.
  const trendable = !isXY && !horizontal;
  const trend = trendable ? ds.trend : undefined;
  const trendKey = trend?.seriesKey ?? ds.series[0]?.key;
  let data: ChartDataset["rows"] = ds.rows;
  const extraSeries: AgCartesianSeriesOptions[] = [];
  if (trend && trendKey) {
    const trendColor = resolveCssColor("hsl(var(--muted-foreground))");
    const ys = seriesNumbers(ds, trendKey);
    if (trend.type === "reference") {
      const ref = referenceValue(ys, trend);
      if (ref != null) {
        yAxis.crossLines = [
          {
            type: "line",
            value: ref,
            stroke: trendColor,
            strokeWidth: 1.5,
            lineDash: [6, 4],
            label: {
              enabled: true,
              text: `${trendLabel(trend)}: ${ds.valueFormat(ref)}`,
              position: "top-left",
              fontSize: 10,
              color: trendColor,
            },
          },
        ];
      }
    } else {
      const vals = trendValues(ys, trend);
      if (vals) {
        data = ds.rows.map((r, i) => ({ ...r, __trend: vals[i] ?? null }));
        extraSeries.push({
          type: "line",
          xKey: ds.categoryKey,
          yKey: "__trend",
          yName: trendLabel(trend),
          stroke: trendColor,
          strokeWidth: 2,
          lineDash: [6, 4],
          marker: { enabled: false },
        });
      }
    }
  }

  // v14: axes are a keyed dictionary ({ x, y }), no longer a positional array.
  const series = isXY
    ? xySeries(ds, chartType, palette)
    : [...cartesianSeries(ds, chartType, palette), ...extraSeries];
  const opts: AgCartesianChartOptions = {
    ...base,
    data,
    series,
    axes: { x: xAxis, y: yAxis },
    legend: { enabled: legendEnabled || extraSeries.length > 0 },
  };
  return opts;
}
